import {Action, ReducerBuilder} from "redux-ts";
import {Reducer} from "platform/redux/Reducer";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import {DepositReduxState} from "core/redux/deposit/DepositReduxState";
import DepositEngine from "core/engine/DepositEngine";
import {
    AddSavedCreditCardType,
    DoSubmitDepositCCType,
    RemoveSavedCreditCardType,
    SetCreditCard,
    SetCreditCardId,
    SetCreditCardIdPayload,
    SetCreditCardPayload,
    SetCreditCardProvider,
    SetCreditCardProviderPayload,
    SetCreditCardValid,
    SetCreditCardValidPayload,
    SetDepositAmount,
    SetDepositConfiguration,
    SetDepositConfigurationPayload,
    SetPaymentMethod,
    SetPaymentMethodPayload,
    SetSavedCreditCards,
    SetSavedCreditCardsPayload,
    SetDepositCurrency,
    CurrencyPayload,
    SelectBankCurrency,
    SelectBankCurrencyPayload,
    SelectBank,
    SelectBankPayload,
    TryChangeCurrencyType, SetReceiptDetails, SetReceiptDetailsPayload, DoSubmitDepositAPMType,
} from "core/redux/deposit/DepositReduxActions";
import {PaymentMethod} from "enum/PaymentMethod";
import Utils from "platform/util/Utils";
import {Currency} from "platform/enum/Currency";
import {TSMap} from "typescript-map";
import {SetAppReadyType} from "platform/redux/core/CoreActions";
import {BankDetail, DepositCurrency, PaymentProvider} from "protocol/response/GetDepositConfigurationsResponse";
import {NumberPayload} from "core/redux/StoreActions";

export const DEF_CREDIT_CARD_LENGTH: number = 20;

export default class DepositReducer extends Reducer<DepositReduxState> {

    private static _instance: DepositReducer;

    public static instance(): DepositReducer {
        return this._instance || (this._instance = new this());
    }

    private constructor() {
        super();
        const depositEngine: DepositEngine = Platform.engine(ServiceType.Deposit);
        this._middlewareActions.set(SetAppReadyType, depositEngine.onAppReady);
        this._middlewareActions.set(TryChangeCurrencyType, depositEngine.onTryChangeCurrency);
        this._middlewareActions.set(DoSubmitDepositCCType, depositEngine.doSubmitDepositCC);
        this._middlewareActions.set(DoSubmitDepositAPMType, depositEngine.doSubmitDepositAPM);
        this._middlewareActions.set(AddSavedCreditCardType, depositEngine.doAddSavedCreditCard);
        this._middlewareActions.set(RemoveSavedCreditCardType, depositEngine.doRemoveSavedCreditCard);
    }

    public get name(): string {
        return "deposit";
    }

    protected setup(builder: ReducerBuilder<DepositReduxState>): void {
        builder
            .init({
                Currencies: new TSMap<Currency, DepositCurrency>(),
                PaymentMethod: PaymentMethod.CreditCard,
                PaymentMethods: [PaymentMethod.CreditCard],
                PaymentProviders: new TSMap<number, PaymentProvider>(),
                supportedCreditCards: [],
                creditCard: {},
                maxCardLength: DEF_CREDIT_CARD_LENGTH,
                Client: {},
                BanksCurrencies: [],
                Banks: new TSMap<Currency, BankDetail[]>(),
                BankSelection: new TSMap<Currency, BankDetail>()
            })
            .handle(SetDepositConfiguration, (state: DepositReduxState, {payload}: Action<SetDepositConfigurationPayload>) => {
                this._logger.debug("Set deposit configuration.");
                const {CreditCards, Currencies, PaymentMethods, PaymentProviders, BanksCurrencies, Banks, BankSelection, Client, PlatformUrl} = payload;
                return Object.assign({}, state, {
                    PaymentMethod: PaymentMethods[0],
                    PaymentMethods,
                    PaymentProviders,
                    Currencies,
                    supportedCreditCards: CreditCards,
                    creditCard: {
                        ...state.creditCard,
                        cardName: `${Utils.nullToEmpty(Client?.FirstName)} ${Utils.nullToEmpty(Client?.LastName)}`
                    },
                    Client: payload.Client,
                    BanksCurrencies,
                    Banks,
                    BankSelection,
                    BankCurrency: BanksCurrencies[0],
                    PlatformUrl
                });
            })
            .handle(SetDepositAmount, (state: DepositReduxState, {payload}: Action<NumberPayload>) => {
                this._logger.debug("Set deposit Amount: " + payload.value);
                return Object.assign({}, state, {
                    Amount: payload.value
                });
            })
            .handle(SetDepositCurrency, (state: DepositReduxState, {payload}: Action<CurrencyPayload>) => {
                this._logger.debug("Set selected currency: " + payload.currency);
                return Object.assign({}, state, {
                    SelectedCurrency: payload.currency
                });
            })
            .handle(SetPaymentMethod, (state: DepositReduxState, {payload}: Action<SetPaymentMethodPayload>) => {
                this._logger.debug("Set payment method: " + payload.paymentMethod);
                return Object.assign({}, state, {
                    PaymentMethod: payload.paymentMethod
                });
            })
            .handle(SetCreditCardProvider, (state: DepositReduxState, {payload}: Action<SetCreditCardProviderPayload>) => {
                this._logger.debug("Set credit card provider: " + payload.creditCardType);
                return Object.assign({}, state, {
                    creditCardType: payload.creditCardType,
                    maxCardLength: payload.maxCardLength || DEF_CREDIT_CARD_LENGTH
                });
            })
            .handle(SetCreditCard, (state: DepositReduxState, {payload}: Action<SetCreditCardPayload>) => {
                return Object.assign({}, state, {
                    creditCard: Utils.merge({}, state.creditCard, payload.creditCard)
                });
            })
            .handle(SetCreditCardValid, (state: DepositReduxState, {payload}: Action<SetCreditCardValidPayload>) => {
                return Object.assign({}, state, {
                    creditCardValid: payload.valid
                });
            })
            .handle(SelectBankCurrency, (state: DepositReduxState, {payload}: Action<SelectBankCurrencyPayload>) => {
                return Object.assign({}, state, {
                    BankCurrency: payload.currency
                });
            })
            .handle(SelectBank, (state: DepositReduxState, {payload}: Action<SelectBankPayload>) => {
                const newState: DepositReduxState = Utils.merge({}, state);
                newState.BankSelection.set(payload.bank.AssetName, payload.bank);
                return newState;
            })
            .handle(SetReceiptDetails, (state: DepositReduxState, {payload}: Action<SetReceiptDetailsPayload>) => {
                return Object.assign({}, state, {
                    receiptDetails: payload.details
                });
            })
            .handle(SetCreditCardId, (state: DepositReduxState, {payload}: Action<SetCreditCardIdPayload>) => {
                return Object.assign({}, state, {
                    creditCardId: payload.creditCardId
                });
            })
            .handle(SetSavedCreditCards, (state: DepositReduxState, {payload}: Action<SetSavedCreditCardsPayload>) => {
                return Object.assign({}, state, {
                    savedCreditCards: payload.savedCreditCards
                });
            });
    }
}
